/**
 * AppTextarea — the app's standard multiline input, built on Fluent UI v9 Textarea + Field.
 *
 * Wraps Fluent's `Field` + `Textarea` pair so the label is semantically associated
 * with the control. Styled via Griffel makeStyles + tokens to match the Utah
 * Health brand (consistent with AppInput and DiscoveryEvalForm field style).
 *
 * No SCSS. No raw HTML inputs.
 */

import {
  Field,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
  type TextareaProps,
} from '@fluentui/react-components';

export interface AppTextareaProps extends Omit<TextareaProps, 'onChange'> {
  /** Semantic label rendered by Fluent Field (required for a11y). */
  label: string;
  value?: string;
  onChange?: (val: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  rows?: number;
  resize?: 'vertical' | 'none' | 'both';
  className?: string;
}

const useStyles = makeStyles({
  textarea: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    ':focus-within': {
      outlineColor: tokens.colorBrandBackground,
    },
  },
});

export function AppTextarea({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  disabled,
  rows = 4,
  resize = 'vertical',
  className,
  ...rest
}: AppTextareaProps) {
  const styles = useStyles();

  return (
    <Field label={label}>
      <Textarea
        value={value}
        onChange={(_, d) => onChange?.(d.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        rows={rows}
        resize={resize}
        className={mergeClasses(styles.textarea, className)}
        {...rest}
      />
    </Field>
  );
}

export default AppTextarea;
